import { useState, useEffect } from "react";
import { apiClient, useAuth, storeSessionToken } from "@/App";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Music2, Calendar, Share2, Upload, Sparkles, ArrowRight, Mail, Lock, Loader2, CheckCircle2, Shield, Monitor, Check, FileDown } from "lucide-react";
import { toast } from "sonner";
import { tokens } from "@/design-system/tokens";
import { MobileInstallBanner } from "@/components/MobileInstallBanner";
import { storeReferral, attachStoredReferral } from "@/lib/referral";

export const Landing = () => {
  const { login, user } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authTab, setAuthTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);

  // Check for returnTo parameter (e.g. after session expiry on account deletion)
  // Check for preview parameter (allows admins to view landing page while logged in)
  const searchParams = new URLSearchParams(window.location.search);
  const returnTo = searchParams.get("returnTo");
  const isPreview = searchParams.get("preview") === "true";
  const refParam = searchParams.get("ref");

  // Persist a referral so it survives the signup round-trip (incl. the Google
  // OAuth redirect). Attribution is applied after the account is created.
  useEffect(() => {
    storeReferral(refParam);
  }, [refParam]);

  useEffect(() => {
    if (returnTo && !user) {
      setShowAuthModal(true);
    }
  }, [returnTo, user]);

  // Update document title when auth modal opens
  useEffect(() => {
    document.title = showAuthModal ? "Sign in — Fretless" : "Fretless";
  }, [showAuthModal]);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: ""
  });

  if (user && !isPreview) {
    if (returnTo === "settings-account") {
      window.location.href = '/settings?tab=account';
    } else {
      window.location.href = '/dashboard';
    }
    return null;
  }

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      toast.error("Please enter your email");
      return;
    }
    
    setLoading(true);
    try {
      const response = await apiClient.post("/auth/forgot-password", {
        email: formData.email
      });
      
      if (response.data.success) {
        setForgotPasswordSent(true);
        toast.success("Check your email for reset instructions");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset email");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error("Please enter email and password");
      return;
    }
    
    setLoading(true);
    try {
      const response = await apiClient.post("/auth/email/login", {
        email: formData.email,
        password: formData.password
      });
      
      if (response.data.success) {
        if (response.data.session_token) storeSessionToken(response.data.session_token);
        const importedCount = response.data.imported_gigs_count || 0;
        toast.success(
          importedCount > 0
            ? `Welcome back! We've added ${importedCount} confirmed ${importedCount === 1 ? 'gig' : 'gigs'} to your account.`
            : "Welcome back!"
        );
        // Redirect to returnTo destination if present (e.g. settings-account after session expiry)
        if (returnTo === "settings-account") {
          window.location.href = '/settings?tab=account';
        } else {
          window.location.href = '/dashboard';
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    console.log("Signup form submitted", { email: formData.email });
    
    if (!formData.email || !formData.password) {
      toast.error("Please enter email and password");
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    try {
      console.log("Attempting signup API call...");
      const response = await apiClient.post("/auth/email/signup", {
        email: formData.email,
        password: formData.password,
        name: formData.name || formData.email.split("@")[0]
      });
      
      console.log("Signup response:", response.data);
      if (response.data.success) {
        if (response.data.session_token) storeSessionToken(response.data.session_token);

        // Show message about imported gigs if any
        const importedCount = response.data.imported_gigs_count || 0;
        let successMessage = "Account created! Welcome to Fretless!";
        if (importedCount > 0) {
          successMessage = `Welcome! We've added ${importedCount} confirmed ${importedCount === 1 ? 'gig' : 'gigs'} to your account.`;
        }
        toast.success(successMessage);

        // Record who referred this new user (best-effort) before leaving.
        await attachStoredReferral(apiClient);

        setShowAuthModal(false);
        window.location.href = '/welcome';
      }
    } catch (error) {
      console.error("Signup error:", error.response?.data || error.message);
      const errorMessage = error.response?.data?.detail || "Failed to create account. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      toast.error("Please enter your email");
      return;
    }
    
    setLoading(true);
    try {
      const response = await apiClient.post("/auth/magic-link/send", {
        email: formData.email
      });
      
      if (response.data.success) {
        setMagicLinkSent(true);
        toast.success("Magic link sent! Check your email.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send magic link");
    } finally {
      setLoading(false);
    }
  };

  // Auth Modal content rendered inline to prevent remounting on state changes
  const authModalContent = (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-card border-border animate-fade-in">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img src="/icons/quaver.png" alt="Fretless" className="h-10 w-auto" />
          </div>
          <CardTitle className="font-heading text-2xl">Welcome to Fretless</CardTitle>
          <CardDescription>Sign in to manage your gigs</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {forgotPasswordSent ? (
            <div className="text-center py-6">
              <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
              <h3 className="font-heading font-semibold text-lg mb-2">Check your email!</h3>
              <p className="text-muted-foreground mb-4">
                If <strong>{formData.email}</strong> is registered, we sent a password reset link.
              </p>
              <p className="text-sm text-muted-foreground">
                The link expires in 1 hour.
              </p>
              <Button 
                variant="ghost" 
                className="mt-4"
                onClick={() => {
                  setForgotPasswordSent(false);
                  setForgotPasswordMode(false);
                }}
              >
                Back to sign in
              </Button>
            </div>
          ) : forgotPasswordMode ? (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <h3 className="font-heading font-semibold text-lg mb-2">Reset Password</h3>
                <p className="text-sm text-muted-foreground">
                  Enter your email and we'll send you a link to reset your password.
                </p>
              </div>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="forgot-email"
                      type="email"
                      className="pl-10"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      data-testid="forgot-email"
                    />
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-primary text-primary-foreground"
                  disabled={loading}
                  data-testid="forgot-submit"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Reset Link"}
                </Button>
                <button
                  type="button"
                  onClick={() => setForgotPasswordMode(false)}
                  className="text-xs text-muted-foreground hover:text-foreground w-full text-center"
                >
                  Back to sign in
                </button>
              </form>
            </div>
          ) : magicLinkSent ? (
            <div className="text-center py-6">
              <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
              <h3 className="font-heading font-semibold text-lg mb-2">Check your email!</h3>
              <p className="text-muted-foreground mb-4">
                We sent a magic link to <strong>{formData.email}</strong>
              </p>
              <p className="text-sm text-muted-foreground">
                Click the link in the email to sign in. The link expires in 15 minutes.
              </p>
              <Button 
                variant="ghost" 
                className="mt-4"
                onClick={() => setMagicLinkSent(false)}
              >
                Use a different email
              </Button>
            </div>
          ) : (
            <>
              {/* Google Sign In */}
              <Button 
                onClick={login}
                variant="outline"
                className="w-full py-6 text-base"
                data-testid="google-login-btn"
              >
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">Or continue with email</span>
                </div>
              </div>

              <Tabs value={authTab} onValueChange={setAuthTab} className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="login">Sign In</TabsTrigger>
                  <TabsTrigger value="signup">Sign Up</TabsTrigger>
                  <TabsTrigger value="magic">Magic Link</TabsTrigger>
                </TabsList>

                {/* Login Tab */}
                <TabsContent value="login" className="space-y-4 mt-4">
                  <form onSubmit={handleEmailLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="login-email"
                          type="email"
                          className="pl-10"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          data-testid="login-email"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="login-password"
                          type="password"
                          className="pl-10"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          data-testid="login-password"
                        />
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-primary text-primary-foreground"
                      disabled={loading}
                      data-testid="login-submit"
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
                    </Button>
                    <button
                      type="button"
                      onClick={() => setForgotPasswordMode(true)}
                      className="text-xs text-primary hover:underline w-full text-center"
                      data-testid="forgot-password-link"
                    >
                      Forgot your password?
                    </button>
                  </form>
                </TabsContent>

                {/* Signup Tab */}
                <TabsContent value="signup" className="space-y-4 mt-4">
                  <form onSubmit={handleEmailSignup} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signup-name">Name (optional)</Label>
                      <Input
                        id="signup-name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        data-testid="signup-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signup-email"
                          type="email"
                          className="pl-10"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          data-testid="signup-email"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signup-password"
                          type="password"
                          className="pl-10"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          data-testid="signup-password"
                        />
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-primary text-primary-foreground"
                      disabled={loading}
                      data-testid="signup-submit"
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Account"}
                    </Button>
                    <p className="text-xs text-center text-muted-foreground">
                      By signing up you agree to our{" "}
                      <Link to="/privacy" className="underline hover:text-primary" data-testid="signup-privacy-link">Privacy Policy</Link>
                    </p>
                  </form>
                </TabsContent>

                {/* Magic Link Tab */}
                <TabsContent value="magic" className="space-y-4 mt-4">
                  <form onSubmit={handleMagicLink} className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Enter your email and we'll send you a magic link to sign in instantly - no password needed.
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="magic-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="magic-email"
                          type="email"
                          className="pl-10"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          data-testid="magic-email"
                        />
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-primary text-primary-foreground"
                      disabled={loading}
                      data-testid="magic-submit"
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Magic Link"}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </>
          )}

          <Button 
            variant="ghost" 
            className="w-full"
            onClick={() => {
              setShowAuthModal(false);
              setFormData({ email: "", password: "", name: "" });
              setAuthTab("login");
              setMagicLinkSent(false);
              setForgotPasswordMode(false);
              setForgotPasswordSent(false);
            }}
          >
            Cancel
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  return (
    // Landing is always dark — the hero photo doesn't read well on the light
    // paper palette. data-theme="dark" re-applies dark token values to this
    // whole subtree even when the global theme is light. Light mode is for
    // the authenticated in-app pages only.
    <div data-theme="dark" className="min-h-screen bg-background text-foreground noise-bg relative overflow-hidden">
      {/* Preview bar — only when an admin opened this via Settings → "View
          Landing Page" (/?preview=true). The normal landing redirects logged-in
          users to /dashboard, so without this there's no way back to the app. */}
      {isPreview && (
        <div
          className="relative z-50 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-primary text-primary-foreground px-4 py-2 text-sm text-center"
          style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.5rem)' }}
        >
          <span className="font-medium">You're previewing the landing page</span>
          <Button asChild size="sm" variant="secondary" className="h-7 gap-1.5" data-testid="exit-landing-preview-btn">
            <Link to="/settings">
              <ArrowRight className="h-3.5 w-3.5 rotate-180" />
              Back to app
            </Link>
          </Button>
        </div>
      )}

      {/* Auth Modal */}
      {showAuthModal && authModalContent}

      {/* Hero Section */}
      <div className="relative">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('/images/landing-hero.jpg')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background" />
        
        {/* Nav */}
        <nav className="relative z-10 glass-nav px-6 py-4" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 1rem)' }}>
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="/icons/quaver.png" alt="Fretless" className="h-8 w-auto" />
              <span style={{ fontFamily: tokens.font.wordmark, fontWeight: 800, fontSize: "24px", letterSpacing: "-0.025em", lineHeight: 1 }}><span style={{ color: tokens.color.text }}>Fret</span><span style={{ color: tokens.color.orange }}>less</span></span>
            </div>
            <Button 
              onClick={() => setShowAuthModal(true)}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider px-6 btn-glow"
              data-testid="login-btn-nav"
            >
              Sign In
            </Button>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-24 pb-32">
          <div className="max-w-3xl">
            <p className="animate-fade-in" style={{ ...tokens.type.kicker, color: tokens.color.orange, marginBottom: `${tokens.space.md}px` }}>
              The Musician's Command Center
            </p>
            <h1 className="md:text-7xl animate-fade-in stagger-1" style={{ ...tokens.type.hero, marginBottom: `${tokens.space.lg}px`, fontSize: "clamp(36px, 8vw, 64px)" }}>
              Your Gigs.
              <br />
              <span style={{ color: tokens.color.orange }}>Organized.</span>
            </h1>
            <p className="max-w-xl animate-fade-in stagger-2" style={{ ...tokens.type.body, color: tokens.color.textDim, marginBottom: `${tokens.space.xl}px`, fontSize: "clamp(14.5px, 2vw, 18px)" }}>
              Track shows, sync calendars, export gigs to Songkick and Bandsintown, and generate social posts — all in one focused workspace built for musicians.
            </p>
            <div className="flex flex-wrap animate-fade-in stagger-3" style={{ gap: `${tokens.space.md}px` }}>
              <Button 
                onClick={() => setShowAuthModal(true)}
                size="lg"
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider px-8 py-6 text-lg btn-glow"
                data-testid="get-started-btn"
              >
                Get Started Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                variant="outline"
                size="lg"
                className="border-border hover:bg-secondary rounded-none uppercase font-medium tracking-wider px-8 py-6 text-lg"
                data-testid="learn-more-btn"
                onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section id="features" className="px-6" style={{ paddingTop: `${tokens.space.xxxl}px`, paddingBottom: `${tokens.space.xxxl}px` }}>
        <div className="max-w-7xl mx-auto">
          <p className="text-center" style={{ ...tokens.type.kicker, color: tokens.color.orange, marginBottom: `${tokens.space.md}px` }}>Features</p>
          <h2 className="text-center" style={{ fontFamily: tokens.font.display, fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 600, marginBottom: `${tokens.space.xxxl}px` }}>
            Everything you need to manage your music career
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4" style={{ gap: `${tokens.space.xl}px` }}>
            {[
              {
                icon: Calendar,
                title: "Gig Calendar",
                description: "Track all your shows in one place. Sync with Google Calendar automatically."
              },
              {
                icon: Sparkles,
                title: "Smart Fill",
                description: "Auto-populate gig details from venue websites and event pages."
              },
              {
                icon: Share2,
                title: "Social Posts",
                description: "Generate AI-powered social media captions for your gigs."
              },
              {
                icon: Upload,
                title: "Gig export for Songkick and Bandsintown",
                description: "Export your shows in the exact CSV format Songkick and Bandsintown need — ready to upload straight to their bulk import tool. No reformatting, no errors."
              }
            ].map((feature, index) => (
              <div key={index} className="hover:border-primary/50 transition-colors" style={{ padding: `${tokens.space.lg}px`, border: `1px solid ${tokens.color.cardBorder}`, borderRadius: `${tokens.radius.lg}px`, backgroundColor: tokens.color.cardBg }}>
                <feature.icon className="h-10 w-10" style={{ color: tokens.color.orange, marginBottom: `${tokens.space.md}px` }} />
                <h3 style={{ fontFamily: tokens.font.display, fontWeight: 600, fontSize: 18, marginBottom: `${tokens.space.sm}px`, color: tokens.color.text }}>{feature.title}</h3>
                <p style={{ color: tokens.color.textDim, fontSize: 14 }}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Songkick / Bandsintown Callout Section */}
      <section className="px-6" style={{ paddingTop: `${tokens.space.xxxl}px`, paddingBottom: `${tokens.space.xxxl}px`, backgroundColor: tokens.color.appBg }} data-testid="export-callout-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          {/* Left — Copy */}
          <div>
            <p style={{ ...tokens.type.kicker, color: tokens.color.orange, marginBottom: `${tokens.space.md}px` }}>
              Built for working musicians
            </p>
            <h2 style={{ fontFamily: tokens.font.display, fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 600, marginBottom: `${tokens.space.lg}px` }}>
              Get your gigs on Songkick and Bandsintown fast
            </h2>
            <p style={{ color: tokens.color.textDim, fontSize: "clamp(14px, 2vw, 18px)", marginBottom: `${tokens.space.xl}px`, maxWidth: tokens.breakpoint.desktop > 900 ? "28rem" : "100%" }}>
              Log your shows in Fretless once. Export a perfectly formatted CSV for Songkick or Bandsintown and upload it straight to their bulk import tool — no spreadsheet wrangling, no format errors, no rejected files.
            </p>
            <div style={{ marginBottom: `${tokens.space.xl}px` }}>
              {[
                "Songkick Tourbox-ready CSV format",
                "Bandsintown bulk upload format",
                "Splits large exports automatically"
              ].map((item, i) => (
                <div key={i} className="flex items-center" style={{ gap: `${tokens.space.md}px`, marginBottom: i < 2 ? `${tokens.space.md}px` : 0 }}>
                  <Check className="h-5 w-5 shrink-0" style={{ color: tokens.color.orange }} />
                  <span style={{ fontSize: 14, color: tokens.color.text }}>{item}</span>
                </div>
              ))}
            </div>
            <p style={{ fontSize: 12, color: tokens.color.textMuted, marginBottom: `${tokens.space.xl}px`, maxWidth: "28rem" }}>
              You upload the file directly to Songkick or Bandsintown — Fretless formats it perfectly so it's accepted first time.
            </p>
            <Button
              onClick={() => setShowAuthModal(true)}
              size="lg"
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider px-8 py-6 text-base btn-glow"
              data-testid="export-callout-cta"
            >
              Get started free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>

          {/* Right — Mock UI */}
          <div className="flex justify-center lg:justify-end">
            <div className="w-full max-w-md overflow-hidden" style={{ borderRadius: `${tokens.radius.xl}px`, border: `1px solid ${tokens.color.cardBorder}`, backgroundColor: tokens.color.cardBg2, boxShadow: tokens.shadow.cardLg }} data-testid="export-mock-ui">
              {/* Mock window chrome */}
              <div className="flex items-center" style={{ gap: `${tokens.space.sm}px`, paddingLeft: `${tokens.space.md}px`, paddingRight: `${tokens.space.md}px`, paddingTop: `${tokens.space.sm}px`, paddingBottom: `${tokens.space.sm}px`, borderBottom: `1px solid ${tokens.color.cardBorder}`, backgroundColor: tokens.color.innerBg }}>
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "rgba(239, 68, 68, 0.6)" }} />
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "rgba(234, 179, 8, 0.6)" }} />
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "rgba(34, 197, 94, 0.6)" }} />
                <span className="ml-3" style={{ fontSize: 12, color: tokens.color.textMuted, fontFamily: tokens.font.mono }}>Fretless — Export</span>
              </div>

              <div style={{ padding: `${tokens.space.lg}px` }}>
                {/* Export buttons */}
                <div style={{ display: "flex", flexDirection: "column", gap: `${tokens.space.md}px` }}>
                  <button className="w-full flex items-center justify-between transition-colors" style={{ paddingLeft: `${tokens.space.md}px`, paddingRight: `${tokens.space.md}px`, paddingTop: `${tokens.space.sm}px`, paddingBottom: `${tokens.space.sm}px`, borderRadius: `${tokens.radius.lg}px`, border: `1px solid ${tokens.color.cardBorder}`, backgroundColor: tokens.color.appBg, cursor: "default" }}>
                    <div className="flex items-center" style={{ gap: `${tokens.space.md}px` }}>
                      <Upload className="h-4 w-4" style={{ color: '#8DC63F' }} />
                      <span style={{ fontSize: 14, fontWeight: 500, color: tokens.color.text }}>Export for Songkick</span>
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", color: '#8DC63F' }}>Songkick</span>
                  </button>
                  <button className="w-full flex items-center justify-between transition-colors" style={{ paddingLeft: `${tokens.space.md}px`, paddingRight: `${tokens.space.md}px`, paddingTop: `${tokens.space.sm}px`, paddingBottom: `${tokens.space.sm}px`, borderRadius: `${tokens.radius.lg}px`, border: `1px solid ${tokens.color.cardBorder}`, backgroundColor: tokens.color.appBg, cursor: "default" }}>
                    <div className="flex items-center" style={{ gap: `${tokens.space.md}px` }}>
                      <Upload className="h-4 w-4" style={{ color: '#00B4B3' }} />
                      <span style={{ fontSize: 14, fontWeight: 500, color: tokens.color.text }}>Export for Bandsintown</span>
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", color: '#00B4B3' }}>Bandsintown</span>
                  </button>
                </div>

                {/* CSV Preview */}
                <div style={{ marginTop: `${tokens.space.sm}px` }}>
                  <div className="flex items-center" style={{ gap: `${tokens.space.sm}px`, marginBottom: `${tokens.space.md}px`, paddingLeft: `${tokens.space.sm}px` }}>
                    <FileDown className="h-4 w-4" style={{ color: tokens.color.textMuted }} />
                    <span style={{ fontSize: 12, color: tokens.color.textMuted, fontFamily: tokens.font.mono }}>fretless-songkick-2026-03-31.csv</span>
                  </div>
                  <p style={{ fontSize: 10, ...tokens.type.kickerSm, color: tokens.color.textMuted, marginBottom: `${tokens.space.sm}px` }}>CSV Preview</p>
                  <div className="overflow-hidden" style={{ borderRadius: `${tokens.radius.lg}px`, border: `1px solid ${tokens.color.cardBorder}`, backgroundColor: tokens.color.innerBg, fontSize: 12 }}>
                    <table className="w-full">
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${tokens.color.cardBorder}` }}>
                          <th style={{ paddingLeft: `${tokens.space.sm}px`, paddingRight: `${tokens.space.sm}px`, paddingTop: `${tokens.space.sm}px`, paddingBottom: `${tokens.space.sm}px`, textAlign: "left", color: tokens.color.textMuted, fontWeight: 500 }}>Date</th>
                          <th style={{ paddingLeft: `${tokens.space.sm}px`, paddingRight: `${tokens.space.sm}px`, paddingTop: `${tokens.space.sm}px`, paddingBottom: `${tokens.space.sm}px`, textAlign: "left", color: tokens.color.textMuted, fontWeight: 500 }}>Venue</th>
                          <th style={{ paddingLeft: `${tokens.space.sm}px`, paddingRight: `${tokens.space.sm}px`, paddingTop: `${tokens.space.sm}px`, paddingBottom: `${tokens.space.sm}px`, textAlign: "left", color: tokens.color.textMuted, fontWeight: 500 }}>Lineup</th>
                        </tr>
                      </thead>
                      <tbody style={{ color: tokens.color.text }}>
                        <tr style={{ borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                          <td style={{ paddingLeft: `${tokens.space.sm}px`, paddingRight: `${tokens.space.sm}px`, paddingTop: `${tokens.space.sm}px`, paddingBottom: `${tokens.space.sm}px` }}>2026-04-12</td>
                          <td style={{ paddingLeft: `${tokens.space.sm}px`, paddingRight: `${tokens.space.sm}px`, paddingTop: `${tokens.space.sm}px`, paddingBottom: `${tokens.space.sm}px` }}>Jazz Cafe, London</td>
                          <td style={{ paddingLeft: `${tokens.space.sm}px`, paddingRight: `${tokens.space.sm}px`, paddingTop: `${tokens.space.sm}px`, paddingBottom: `${tokens.space.sm}px` }}>The Quartet</td>
                        </tr>
                        <tr style={{ borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                          <td style={{ paddingLeft: `${tokens.space.sm}px`, paddingRight: `${tokens.space.sm}px`, paddingTop: `${tokens.space.sm}px`, paddingBottom: `${tokens.space.sm}px` }}>2026-04-19</td>
                          <td style={{ paddingLeft: `${tokens.space.sm}px`, paddingRight: `${tokens.space.sm}px`, paddingTop: `${tokens.space.sm}px`, paddingBottom: `${tokens.space.sm}px` }}>Ronnie Scott's</td>
                          <td style={{ paddingLeft: `${tokens.space.sm}px`, paddingRight: `${tokens.space.sm}px`, paddingTop: `${tokens.space.sm}px`, paddingBottom: `${tokens.space.sm}px` }}>Solo Set</td>
                        </tr>
                        <tr>
                          <td style={{ paddingLeft: `${tokens.space.sm}px`, paddingRight: `${tokens.space.sm}px`, paddingTop: `${tokens.space.sm}px`, paddingBottom: `${tokens.space.sm}px` }}>2026-05-03</td>
                          <td style={{ paddingLeft: `${tokens.space.sm}px`, paddingRight: `${tokens.space.sm}px`, paddingTop: `${tokens.space.sm}px`, paddingBottom: `${tokens.space.sm}px` }}>Vortex Jazz Club</td>
                          <td style={{ paddingLeft: `${tokens.space.sm}px`, paddingRight: `${tokens.space.sm}px`, paddingTop: `${tokens.space.sm}px`, paddingBottom: `${tokens.space.sm}px` }}>Duo w/ Sam</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section style={{ paddingTop: `${tokens.space.xxxl}px`, paddingBottom: `${tokens.space.xxxl}px`, paddingLeft: `${tokens.space.lg}px`, paddingRight: `${tokens.space.lg}px`, borderTop: `1px solid ${tokens.color.innerBorder}` }}>
        <div className="max-w-3xl mx-auto flex flex-wrap items-center justify-center gap-8">
          <div className="flex items-center gap-2" style={{ fontSize: "14px", color: tokens.color.textMuted }}>
            <Shield className="h-4 w-4" style={{ color: tokens.color.orange }} />
            <span>Secured with 256-bit encryption</span>
          </div>
          <div className="flex items-center gap-2" style={{ fontSize: "14px", color: tokens.color.textMuted }}>
            <Monitor className="h-4 w-4" style={{ color: tokens.color.orange }} />
            <span>Works on any device — no app store needed</span>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{ paddingTop: `${tokens.space.xxxl}px`, paddingBottom: `${tokens.space.xxxl}px`, paddingLeft: `${tokens.space.lg}px`, paddingRight: `${tokens.space.lg}px`, backgroundColor: `rgba(245,137,31,0.05)` }}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 style={{ fontFamily: tokens.font.display, fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 700, marginBottom: `${tokens.space.lg}px`, color: tokens.color.text }}>
            Ready to organize your gigs?
          </h2>
          <p style={{ ...tokens.type.body, color: tokens.color.textDim, marginBottom: `${tokens.space.xl}px` }}>
            Join musicians who use Fretless to manage their gigs, promote their shows, and never miss a date.
          </p>
          <Button
            onClick={() => setShowAuthModal(true)}
            size="lg"
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider px-8 py-6 text-lg btn-glow"
            data-testid="cta-get-started"
          >
            Get Started Free
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ paddingTop: `${tokens.space.xxxl}px`, paddingBottom: `${tokens.space.xxxl}px`, paddingLeft: `${tokens.space.lg}px`, paddingRight: `${tokens.space.lg}px`, borderTop: `1px solid ${tokens.color.innerBorder}` }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/icons/quaver.png" alt="Fretless" className="h-6 w-auto" />
            <span style={{ fontFamily: tokens.font.wordmark, fontWeight: 800, fontSize: "20px", letterSpacing: "-0.025em", lineHeight: 1 }}><span style={{ color: tokens.color.text }}>Fret</span><span style={{ color: tokens.color.orange }}>less</span></span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/privacy" style={{ color: tokens.color.textMuted, fontSize: "14px", transition: "color 150ms ease" }} className="hover:text-primary" data-testid="footer-privacy-link">Privacy Policy</Link>
            <p style={{ color: tokens.color.textMuted, fontSize: "14px", margin: 0 }}>
              © {new Date().getFullYear()} Fretless. Built for musicians.
            </p>
          </div>
        </div>
      </footer>
      <MobileInstallBanner />
    </div>
  );
};

export default Landing;
